import { Button, Col, DatePicker, Form, notification, Row } from "antd";
import { Moment } from "moment";
import React, { useState } from "react";
import {
  ManagementStudyDates,
  SaveStudyDatesParams,
} from "types/datesManagement.types";
import { useTranslation } from "react-i18next";
import { useSaveStudyDatesMutation } from "store/datesManagement";
import ConfirmationModal from "views/App/components/Modals/ConfirmationModal";

interface Props {
  initialValues: FormValuesType;
  data: ManagementStudyDates[];
  refetchData(): void;
}

interface FormValuesType {
  [key: string]: Moment;
}

function DatesManagementForm({
  initialValues,
  data,
  refetchData,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const [datesManagementForm] = Form.useForm();
  const [confirmationModalVisible, setConfirmationModalVisible] =
    useState(false);
  const [changedValueIds, setChangedValueIds] = useState<string[] | null>(null);
  const [saveStudyDates, saveStudyDatesResult] = useSaveStudyDatesMutation();

  const handleValuesChange = (changedValues: FormValuesType) => {
    let changed: string[] = changedValueIds ? [...changedValueIds] : [];
    Object.keys(changedValues).forEach((key) => {
      if (!changedValues[key].isSame(initialValues[key])) {
        changed.push(key);
      } else {
        changed = changed.filter((el) => el !== key);
      }
    });

    const uniques = Array.from(new Set(changed));
    setChangedValueIds(uniques);
  };

  const handleFormSubmit = async () => {
    setConfirmationModalVisible(true);
  };

  const handleConfirm = async () => {
    const values = datesManagementForm.getFieldsValue();
    const preparedBody: SaveStudyDatesParams[] = [];
    const onlyChangedValues = Object.keys(values).filter(
      (key) => changedValueIds && changedValueIds.includes(key)
    );
    onlyChangedValues.forEach((key) =>
      preparedBody.push({
        id: Number(key),
        date: values[key].format("YYYY-MM-DDTHH:mm:ss").toString(),
      })
    );

    try {
      const payload = await saveStudyDates(preparedBody).unwrap();

      if (!payload.hasErrors) {
        notification.success({
          message: t("dates_management.message.success"),
        });
        refetchData();
      } else {
        datesManagementForm.resetFields();
        notification.error({ message: payload.errorDetails.errorMessage });
      }
    } catch (error) {
      datesManagementForm.resetFields();
      console.error("Error", error);
    } finally {
      setChangedValueIds([]);
      setConfirmationModalVisible(false);
    }
  };

  return (
    <>
      <Form
        id="date_management"
        form={datesManagementForm}
        onFinish={handleFormSubmit}
        initialValues={initialValues}
        labelCol={{ style: { width: "50%" } }}
        labelAlign="left"
        onValuesChange={handleValuesChange}
      >
        <Col span={10}>
          {data?.map((item) => (
            <Form.Item
              key={item.id}
              label={t(`dates_management.label.${item.studyDueDateTypeString}`)}
              name={item.id}
            >
              <DatePicker
                disabled={!item.editable}
                allowClear={false}
                style={{ width: "100%" }}
                format="DD.MM.YYYY"
              />
            </Form.Item>
          ))}
        </Col>
        <Row>
          <Col span={10} offset={5}>
            <Button
              type="primary"
              htmlType="submit"
              style={{ width: "50%", margin: "24px 0 24px" }}
              loading={saveStudyDatesResult.isLoading}
              block
              disabled={
                changedValueIds === null || changedValueIds.length === 0
              }
            >
              {t("save")}
            </Button>
          </Col>
        </Row>
      </Form>
      <ConfirmationModal
        visible={confirmationModalVisible}
        isLoading={false}
        title={t("dates_management.modal.title")}
        description={t("dates_management.modal.description")}
        onClose={() => setConfirmationModalVisible(false)}
        onConfirm={handleConfirm}
      />
    </>
  );
}

export default DatesManagementForm;
